/**
 * Weight-scale Settings page — pair a Bluetooth or wired USB scale,
 * live-test the reading, forget it. (Iter 82)
 *
 * Route: /settings/scale (admin) + /staff-app/scale (staff-app)
 */
import React, { useState, useCallback, useRef, useEffect } from "react";
import { toast } from "sonner";
import {
  Scales, Bluetooth, Usb, XCircle, Warning, TestTube, Plug,
} from "@phosphor-icons/react";
import {
  startScaleStream, disconnectScale, scaleCapabilities,
  whyScaleUnavailable, lastTransport, lastDeviceLabel,
} from "../../lib/scaleReader";

const TRANSPORTS = [
  { key: "ble",    label: "Bluetooth scale", icon: Bluetooth,
    hint: "For BLE weighing scales (SIG 0x181D or ASCII-notify)." },
  { key: "serial", label: "USB-Serial scale", icon: Usb,
    hint: "Counter-top scale wired via USB-serial adapter (9600 baud)." },
  { key: "hid",    label: "USB HID scale",   icon: Plug,
    hint: "Kitchen / lab scales that enumerate as HID usage-page 0x8D." },
];

export default function ScaleSettings() {
  const [device, setDevice] = useState(lastDeviceLabel());
  const [transport, setTransport] = useState(lastTransport() || "ble");
  const [status, setStatus] = useState("");
  const [live, setLive] = useState(null);   // { kg, stable, unit, raw }
  const [streaming, setStreaming] = useState(false);
  const stopRef = useRef(null);
  const why = whyScaleUnavailable();
  const caps = scaleCapabilities();

  const stop = useCallback(async () => {
    if (stopRef.current) {
      try { await stopRef.current(); } catch (_) { /* noop */ }
      stopRef.current = null;
    }
    setStreaming(false);
    setStatus("");
  }, []);

  useEffect(() => () => { stop(); disconnectScale(); }, [stop]);

  const start = async (chosen = transport) => {
    if (why) { toast.error(why); return; }
    await stop();
    setLive(null);
    setStreaming(true);
    try {
      const s = await startScaleStream({
        transport: chosen,
        onReading: (r) => setLive(r),
        onStatus:  (m) => setStatus(m),
      });
      stopRef.current = s;
      setDevice(lastDeviceLabel());
      setTransport(chosen);
      toast.success(`Scale paired · ${lastDeviceLabel()}`);
    } catch (e) {
      setStreaming(false);
      setStatus("");
      toast.error(e?.message || "Could not connect");
    }
  };

  const onForget = async () => {
    await stop();
    await disconnectScale();
    localStorage.removeItem("rescigo.scale.transport");
    localStorage.removeItem("rescigo.scale.device");
    setDevice(""); setLive(null);
    toast.success("Scale forgotten");
  };

  return (
    <div className="max-w-3xl mx-auto p-4 md:p-8 space-y-6" data-testid="scale-settings">
      <div>
        <div className="text-[10px] uppercase tracking-[0.22em] text-[#8A8780]">Devices</div>
        <h1 className="font-heading text-2xl md:text-3xl text-[#1A1A1A]">Weight scale</h1>
        <p className="text-sm text-[#5C5A55] mt-1 max-w-xl">
          Pair a Bluetooth scale or plug in a USB-serial counter scale — the Field POS will
          then auto-fill the weight for every bag you scan.
        </p>
      </div>

      {why && (
        <div className="rounded-lg border border-[#F4C7B9] bg-[#F9EDE7] p-4 text-sm text-[#7A2E18] flex items-start gap-2" data-testid="scale-unsupported">
          <Warning size={18} weight="fill" className="mt-0.5 shrink-0"/>
          <div><strong>Not available:</strong> {why}</div>
        </div>
      )}

      {/* Current pairing card */}
      <div className="bg-white border border-[#E5E2D9] rounded-lg p-5">
        <div className="flex items-start gap-4">
          <div className="w-12 h-12 rounded-md bg-[#DDE7F1] flex items-center justify-center shrink-0">
            <Scales size={22} color="#1F3A57" weight="fill" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">Paired scale</div>
            <div className="font-heading text-lg" data-testid="scale-device-name">
              {device || "No scale paired"}
            </div>
            {device && (
              <div className="text-[11px] text-[#1F3A57] mt-1">
                Transport: <strong>{transport.toUpperCase()}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Live reading meter */}
        <div className={`mt-4 rounded-md border ${live?.stable ? "border-[#2D5A27] bg-[#F0F7EE]" : "border-[#E5E2D9] bg-[#F7F6F2]"} p-5 flex items-baseline gap-3`}
             data-testid="scale-live-reading">
          <div className="text-4xl font-heading tabular-nums" style={{ minWidth: "6ch" }}>
            {live ? Number(live.kg).toFixed(3) : "0.000"}
          </div>
          <div className="text-sm text-[#5C5A55]">kg</div>
          {live?.stable && (
            <span className="ml-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#2D5A27] bg-[#D4E0D1] px-1.5 py-0.5 rounded">
              stable
            </span>
          )}
          {live && !live.stable && (
            <span className="ml-2 text-[10px] uppercase tracking-[0.18em] font-semibold text-[#7A5A11] bg-[#F9E4B7] px-1.5 py-0.5 rounded">
              moving
            </span>
          )}
          {streaming && !live && (
            <span className="ml-2 text-[10px] uppercase tracking-[0.18em] text-[#5C5A55]">
              waiting for first reading…
            </span>
          )}
        </div>
        {status && <div className="mt-1 text-[11px] text-[#5C5A55]">{status}</div>}

        <div className="mt-4 flex flex-wrap gap-2">
          <button onClick={() => start(transport)}
                  disabled={!!why || streaming}
                  data-testid="scale-start-btn"
                  className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-[#1F3A57] text-white text-sm font-medium hover:bg-[#15283B] disabled:opacity-60">
            <TestTube size={14} weight="fill" />
            {streaming ? "Reading…" : device ? "Reconnect & read" : "Pair & read"}
          </button>
          {streaming && (
            <button onClick={stop}
                    data-testid="scale-stop-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#E5E2D9] text-sm hover:bg-[#F2F0E9]">
              Stop reading
            </button>
          )}
          {device && (
            <button onClick={onForget}
                    data-testid="scale-forget-btn"
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md border border-[#E5E2D9] text-sm text-[#7A2E18] hover:bg-[#F9EDE7]">
              <XCircle size={14} weight="fill" />
              Forget scale
            </button>
          )}
        </div>
      </div>

      {/* Transport chooser */}
      <div className="bg-white border border-[#E5E2D9] rounded-lg p-5">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A8780]">Choose transport</div>
        <div className="font-heading text-lg mt-0.5">How is the scale connected?</div>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          {TRANSPORTS.map(t => {
            const supported = caps[t.key];
            const Icon = t.icon;
            const active = transport === t.key;
            return (
              <button key={t.key}
                      onClick={() => { setTransport(t.key); start(t.key); }}
                      disabled={!supported}
                      data-testid={`scale-transport-${t.key}`}
                      className={`text-left p-4 border-2 rounded-md transition-colors ${
                        active ? "border-[#1F3A57] bg-[#F1F5FA]" : "border-[#E5E2D9] hover:border-[#B7B3A6]"
                      } ${!supported ? "opacity-50 cursor-not-allowed" : ""}`}>
                <div className="flex items-center gap-2">
                  <Icon size={18} weight={active ? "fill" : "regular"} color={active ? "#1F3A57" : "#5C5A55"}/>
                  <span className="font-heading text-sm">{t.label}</span>
                </div>
                <div className="text-[11px] text-[#5C5A55] mt-1">{t.hint}</div>
                {!supported && (
                  <div className="text-[10px] text-[#C25D38] mt-1">
                    Not supported by this browser
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tips */}
      <div className="bg-[#F7F6F2] border border-[#E5E2D9] rounded-lg p-5 text-xs text-[#5C5A55] leading-relaxed">
        <div className="font-heading text-sm text-[#1A1A1A] mb-2">Pairing tips</div>
        <ul className="list-disc pl-4 space-y-1">
          <li>If the OS pops a pairing PIN, try <code>0000</code> or <code>1234</code>. Rescigo talks to the scale over BLE — the OS dialog is only a first-time formality.</li>
          <li>For a wired USB scale, plug in the USB-serial adapter first, then hit "USB-Serial scale" and pick the right COM port.</li>
          <li>Most cheap scales stream weight only after you press the "Send" / <code>SEND</code> or the print button on the scale itself.</li>
          <li>iOS Safari cannot talk to Bluetooth / USB devices at all — use Android Chrome or a Chrome/Edge laptop.</li>
          <li>Only one browser tab at a time can own the scale connection.</li>
        </ul>
      </div>

      {/* Common blockers — HC-05 / Classic BT explainer */}
      <div className="bg-[#F9EDE7] border border-[#F4C7B9] rounded-lg p-5 text-xs text-[#7A2E18] leading-relaxed"
           data-testid="scale-tips-classic-bt">
        <div className="font-heading text-sm text-[#7A2E18] mb-2 inline-flex items-center gap-1.5">
          <Warning size={14} weight="fill"/>Not seeing your scale in the Bluetooth picker?
        </div>
        <div className="mb-2">
          <strong>Web Bluetooth only supports BLE (Bluetooth Low Energy).</strong>
          Older Classic-Bluetooth modules like <code>HC-05</code> and <code>HC-06</code>
          use SPP (Serial Port Profile), which no browser (Chrome, Edge, Safari, Firefox)
          can see — it's a Bluetooth-radio limitation, not an app bug.
        </div>
        <div className="mb-2 font-semibold text-[#1A1A1A]">What works vs what doesn't:</div>
        <table className="w-full text-[11px] border border-[#F4C7B9]/60 rounded overflow-hidden mb-2">
          <thead className="bg-white/60">
            <tr>
              <th className="text-left p-1.5">Module</th>
              <th className="text-left p-1.5">Type</th>
              <th className="text-left p-1.5">Works?</th>
            </tr>
          </thead>
          <tbody>
            {[
              ["HC-05, HC-06", "Classic BT (SPP)", "❌ No"],
              ["HM-10, HC-08, HC-42", "BLE (SPP-like)", "✅ Yes"],
              ["JDY-08, JDY-33, AT-09, CC41-A, BT-05", "BLE", "✅ Yes"],
              ["Any USB-serial scale", "Wired", "✅ Yes (pick USB-Serial tile)"],
            ].map(([m, t, ok]) => (
              <tr key={m} className="border-t border-[#F4C7B9]/60">
                <td className="p-1.5 font-mono">{m}</td>
                <td className="p-1.5">{t}</td>
                <td className="p-1.5">{ok}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="mb-1 font-semibold text-[#1A1A1A]">Fixing an HC-05 setup — pick one:</div>
        <ol className="list-decimal pl-4 space-y-1">
          <li><strong>Swap the module:</strong> Replace HC-05 with an <code>HM-10</code> / <code>HC-08</code> — same footprint, ₹150–250, no code change needed.</li>
          <li><strong>Skip BT entirely:</strong> Wire the scale's TX/GND to a USB-to-TTL adapter (CP2102 / CH340) and plug into a Chrome/Edge laptop — then pick the <em>USB-Serial scale</em> tile above.</li>
          <li><strong>Manual entry:</strong> Type the weight into the POS scan modal like before — always available as fallback.</li>
        </ol>
      </div>
    </div>
  );
}
